import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { GRAPHQL_OPERATION_NAMES, HttpService } from "./http.service";
import { SubSink } from "subsink";
import { PatientEstimateEntry, TreatmentPlanEntry } from "@apps/patient-core/src/app/data_model/treatment-plan";
import { PatientEstimateBase } from "@backend/graph/patient_estimates/patient-estimate-base";

export enum E_TreatmentPlanStatus {
  LOADING = "LOADING",
  LOADED = "LOADED",
  ERROR_LOADING = "ERROR_LOADING",
  SAVING = "SAVING",
  ERROR_SAVING = "ERROR_SAVING",
  ERROR_DOWNLOADING = "ERROR_DOWNLOADING",
  SAVED = "SAVED",
}

const COMMON_QUERY = `
  id
  treatment_plan_id
  treatment_plan_completed
  signed
  description
  created_at
  template_name
  footnote
  payment_terms
  hide_estimate_items
  hide_insurance_splits
`;

@Injectable({
  providedIn: "root",
})
export class TreatmentPlansService {
  public onTreatmentPlansChanged: BehaviorSubject<{
    status: E_TreatmentPlanStatus;
    data?: Array<TreatmentPlanEntry>;
  } | null> = new BehaviorSubject(null);
  public onTreatmentPlanEstimateChanged: BehaviorSubject<{
    status: E_TreatmentPlanStatus;
    estimate?: PatientEstimateEntry;
    estimate_id?: number;
  } | null> = new BehaviorSubject(null);
  public rawEstimates: Array<PatientEstimateBase>;
  private _treatmentPlans: Array<TreatmentPlanEntry> | null = null;
  private _cache = {};
  private _subs = new SubSink();
  private _isFetching: boolean;

  constructor(private _httpService: HttpService) {}

  public set treatmentPlans(treatmentPlans: Array<TreatmentPlanEntry> | null) {
    this._treatmentPlans = treatmentPlans;
  }

  public get treatmentPlans(): Array<TreatmentPlanEntry> | null {
    return this._treatmentPlans;
  }

  public get openTreatmentPlans(): Array<TreatmentPlanEntry> {
    return this.treatmentPlans?.filter((t) => !t.treatment_plan_completed) || [];
  }

  public get closedTreatmentPlans(): Array<TreatmentPlanEntry> {
    return this.treatmentPlans?.filter((t) => t.treatment_plan_completed) || [];
  }

  getTreatmentPlans(force = false): void {
    const QUERY = () => `
      {
        patient {
          estimates {
            items {
              ${COMMON_QUERY}
            }
          }
        }
      }
    `;

    if (this._isFetching) return;
    if (force) this.treatmentPlans = null;
    if (this.treatmentPlans) {
      this.onTreatmentPlansChanged.next({
        status: E_TreatmentPlanStatus.LOADED,
        data: this.treatmentPlans,
      });
    } else {
      this.onTreatmentPlansChanged.next({ status: E_TreatmentPlanStatus.LOADING });
      this._isFetching = true;
      this._subs.sink = this._httpService.query(GRAPHQL_OPERATION_NAMES.ESTIMATE, QUERY()).subscribe(
        (result) => {
          if (result.errors) {
            this.onTreatmentPlansChanged.next({
              status: E_TreatmentPlanStatus.ERROR_LOADING,
            });
            this._isFetching = false;
          }
          this.treatmentPlans = this._groupEstimatesByTreatmentPlan(result.data.patient.estimates.items);
          this.onTreatmentPlansChanged.next({
            status: E_TreatmentPlanStatus.LOADED,
            data: this.treatmentPlans,
          });
          this._isFetching = false;
        },
        () => {
          this.onTreatmentPlansChanged.next({
            status: E_TreatmentPlanStatus.ERROR_LOADING,
          });
          this._isFetching = false;
        }
      );
    }
  }

  private _groupEstimatesByTreatmentPlan(estimates: Array<PatientEstimateBase>): Array<TreatmentPlanEntry> {
    this.rawEstimates = estimates;
    const entries = new Array<TreatmentPlanEntry>();
    estimates = estimates.filter((e) => e.treatment_plan_id);

    for (const estimate of estimates) {
      const { treatment_plan_id } = estimate;
      const entry = entries.find((e) => e.treatment_plan_id === treatment_plan_id);
      if (entry) {
        entry.estimates.push(new PatientEstimateEntry(estimate));
      } else {
        const newEntry = new TreatmentPlanEntry();
        if (treatment_plan_id) newEntry.treatment_plan_id = treatment_plan_id;
        newEntry.estimates = new Array<PatientEstimateEntry>();
        newEntry.estimates.push(new PatientEstimateEntry(estimate));
        entries.push(newEntry);
      }
    }
    return entries;
  }

  getEstimate(estimate_id: number, force = false): void {
    if (force) this._cache[`estimate_${estimate_id}`] = null;
    if (this._cache[`estimate_${estimate_id}`]) {
      this.onTreatmentPlanEstimateChanged.next({
        status: E_TreatmentPlanStatus.LOADED,
        estimate: this._cache[`estimate_${estimate_id}`],
        estimate_id,
      });
    } else {
      const QUERY = () => `
      {
        patient {
          estimate(estimate_id: ${estimate_id}) {
            ${COMMON_QUERY}
            estimate_items {
              price
              estimate_id
              appointment_index
              description
              id
              position
              tooth_location
              canada_supplementary_items_for_estimate
              {
                id
                name
                price
                code
                insurance_rebate_value
                primary_coverage_amount
                secondary_coverage_amount
              }
              insurance_rebate_value
              coverages {
                coverage_type
                carrier_name
                coverage_amount
                coverage_percentage
                source
                coverage_calculated_at
              }
            }
          }
        }
      }
    `;

      this._subs.sink = this._httpService.query(GRAPHQL_OPERATION_NAMES.ESTIMATE, QUERY()).subscribe(
        (response) => {
          if (response.errors) {
            this.onTreatmentPlanEstimateChanged.next({
              status: E_TreatmentPlanStatus.ERROR_LOADING,
              estimate_id,
            });
          }
          const { estimate } = response.data.patient;
          this._cache[`estimate_${estimate_id}`] = estimate;
          this.onTreatmentPlanEstimateChanged.next({
            status: E_TreatmentPlanStatus.LOADED,
            estimate,
            estimate_id,
          });
        },
        () => {
          this.onTreatmentPlanEstimateChanged.next({
            status: E_TreatmentPlanStatus.ERROR_LOADING,
            estimate_id,
          });
        }
      );
    }
  }

  public signEstimate(estimate_id: number, encoded_signature: string): void {
    const QUERY = () => `
      {
        createEstimate(new_item: {
          estimate_id: "${estimate_id}",
          encoded_signature: "${encoded_signature}",
        }) {
          ${COMMON_QUERY}
          estimate_items {
            price
            estimate_id
            appointment_index
            description
            id
            position
            tooth_location
            canada_supplementary_items_for_estimate
            {
              id
              name
              price
              code
              insurance_rebate_value
              primary_coverage_amount
              secondary_coverage_amount
            }
            insurance_rebate_value
            coverages {
              coverage_type
              carrier_name
              coverage_amount
              coverage_percentage
              source
              coverage_calculated_at
            }
          }
        }
      }
    `;

    this._subs.sink = this._httpService.mutation("createEstimate", QUERY()).subscribe(
      (response) => {
        if (response.data.createEstimate.id) {
          this._cache[`estimate_${estimate_id}`] = response.data.createEstimate;
          this.onTreatmentPlanEstimateChanged.next({
            status: E_TreatmentPlanStatus.SAVED,
            estimate_id,
          });
        }
        this.getTreatmentPlans(true);
      },
      () => {
        this.getTreatmentPlans(true);
      }
    );
  }
}
